import { Button, Dropdown, DropdownMenu, DropdownMenuItem, EditIcon, ImportIcon, Row, Table } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  FunctionalClassSubstitutionBehavior,
  TimetableReportType,
  use_UpdateClassesAttendancePlanShowInSubstitutionPlanMutation,
} from '../../../types/planung-graphql-client-defs';
import { blockIfEventJobRunning, createEvents } from '../../../utils/eventsApi';
import { createTimetableCollectiveReport, downloadReport } from '../../../utils/pdfReport';
import { showUserErrorToast } from '../../../utils/toast';
import { useTimetableBlocks } from '../../TimetableBlock/hooks/useTimetableBlocks';
import { useClassesTable } from '../hooks/useClassesTable';
import { ClassesTableType } from '../types/ClassesTypes';

export const FunctionClassesTable: FC = observer(() => {
  const { t } = useTranslation();
  const selectedSchoolYear = useSelectedSchoolYear();
  const { pimAuthClaims } = useAuthClaims();
  const context = useMemorizedCacheTag('CLASSES');

  const [, updateAttendancePlanAndShowInPlan] = use_UpdateClassesAttendancePlanShowInSubstitutionPlanMutation();

  const today = dayjs().format('YYYY-MM-DD');

  const [switchLoading, setSwitchLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const { timetableBlocks: currentTimetableBlocks } = useTimetableBlocks(selectedSchoolYear?.uuid, { date: today });

  const {
    data,
    columns,
    tableSettings,
    tableSorting,
    tableSelection,
    tableVisibility,
    printerSettings,
    actionBarSettings,
  } = useClassesTable({
    context: 'function',
    columns: ['name', 'shortName', 'room', 'color', 'functionalClassSubstitutionBehavior'],
  });

  const functionClasses = data.filter((c) => c.functionClass && c.relevantForSubstitution);

  const currentTimetableBlockUuid = useMemo(() => {
    return currentTimetableBlocks?.[0]?.uuid;
  }, [currentTimetableBlocks]);

  /**
   * `functionalClassSubstitutionBehavior` entscheidet in createEventsMapFromTimetableCardData mit,
   * ob für die Funktionsklasse Events entstehen (ATTENDANCE_PLAN_SOURCE fliegt dort raus). Das
   * Update allein legt keine an bzw. räumt keine ab — der EventJob muss angestoßen werden, sonst
   * passt der Vertretungsplan erst nach dem nächtlichen Lauf wieder zur Einstellung.
   */
  const setSubstitutionBehavior = useCallback(
    async (uuid: string, behavior: FunctionalClassSubstitutionBehavior | null) => {
      if (await blockIfEventJobRunning()) return;

      setSwitchLoading(true);
      const { error } = await updateAttendancePlanAndShowInPlan(
        { uuid, functionalClassSubstitutionBehavior: behavior },
        context,
      );

      if (error) {
        showUserErrorToast({ error });
      } else if (selectedSchoolYear) {
        await createEvents({
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
          schoolYearUuid: selectedSchoolYear.uuid,
        });
      }
      setSwitchLoading(false);
    },
    [updateAttendancePlanAndShowInPlan, context, pimAuthClaims, selectedSchoolYear],
  );

  return (
    <>
      <Table<ClassesTableType>
        data={functionClasses}
        columns={columns}
        {...tableSettings}
        {...tableSelection}
        {...tableSorting}
        {...tableVisibility}
        printerSettings={{
          ...printerSettings,
          subline: `${t('functionClass.attendanceClass', { count: 2 })} - ${t('common.schoolYear')} ${selectedSchoolYear?.shortName}`,
          filename: `${t('functionClass.attendanceClass', { count: 2 })}_${selectedSchoolYear?.shortName}`,
        }}
        actionBarSettings={{
          ...actionBarSettings,
          showBulkEdit: false,
          extendedActionsRight: (
            <Button
              hierarchy='tertiary'
              disabled={!currentTimetableBlockUuid}
              isLoading={downloadLoading}
              onClick={() => {
                setDownloadLoading(true);
                if (currentTimetableBlockUuid) {
                  createTimetableCollectiveReport(
                    currentTimetableBlockUuid,
                    'timetable',
                    TimetableReportType.AttendanceSchedule,
                  )
                    .then(async (value) => {
                      void downloadReport(
                        {
                          uuid: value.uuid,
                          name: `${t('classes.currentAttendanceSchedule', { count: 1 })}-${dayjs().format('YYYY-MM-DD')}`,
                          contextUuid: 'is-ignored',
                          contextName: 'is-ignored',
                          timestamp: dayjs(),
                        },
                        TimetableReportType.AttendanceSchedule,
                      );
                    })
                    .catch((error) => {
                      showUserErrorToast({ error });
                    })
                    .finally(() => {
                      setDownloadLoading(false);
                    });
                }
              }}
              icon={<ImportIcon />}
            >
              {t('classes.currentAttendanceSchedule', { count: 1 })}
            </Button>
          ),
        }}
        lastCol={(row) => {
          return (
            <Dropdown trigger={<Button hierarchy='ghost' icon={<EditIcon />} />}>
              <DropdownMenu
                data={[
                  {
                    type: 'switch',
                    label: t('functionClass.showInSubstitutionPlan', { count: 1 }),
                    tooltip: t('functionClass.showInSubstitutionPlanTooltip'),
                    disabled: switchLoading,
                    onValueChange: async (value) => {
                      await setSubstitutionBehavior(
                        row.original.uuid,
                        value ? ('SHOW_IN_SUBSTITUTION_PLAN' as FunctionalClassSubstitutionBehavior) : null,
                      );
                    },
                    value: row.original.functionalClassSubstitutionBehavior === 'SHOW_IN_SUBSTITUTION_PLAN',
                  },
                  {
                    type: 'switch',
                    disabled: switchLoading,
                    tooltip: t('functionClass.normalClassBehaviorTooltip'),
                    label: t('functionClass.normalClassBehavior', { count: 1 }),
                    onValueChange: async (value) => {
                      await setSubstitutionBehavior(
                        row.original.uuid,
                        value ? ('NORMAL_CLASS_BEHAVIOR' as FunctionalClassSubstitutionBehavior) : null,
                      );
                    },
                    value: row.original.functionalClassSubstitutionBehavior === 'NORMAL_CLASS_BEHAVIOR',
                  },
                  {
                    type: 'switch',
                    disabled: switchLoading,
                    tooltip: t('functionClass.attendanceClassBehaviorTooltip'),
                    label: t('functionClass.attendanceClass', { count: 1 }),
                    onValueChange: async (value) => {
                      await setSubstitutionBehavior(
                        row.original.uuid,
                        value ? ('ATTENDANCE_PLAN_SOURCE' as FunctionalClassSubstitutionBehavior) : null,
                      );
                    },
                    value: row.original.functionalClassSubstitutionBehavior === 'ATTENDANCE_PLAN_SOURCE',
                  },
                ]}
              />
            </Dropdown>
          );
        }}
      />
    </>
  );
});
